import { CardapioItem } from "./cardapio";

let sacolaLinhaSequencia = 0;

export const SACOLA_QUANTIDADE_MAXIMA = 99;

// Quantidade DIGITADA no campo da sacola. O touchspin do `@core` so respeita o maximo nos
// botoes + e -: o que se digita chega cru (e como texto), e o backend aceita qualquer
// numero. Foi assim que entrou um pedido de 3e+33 latas (0010402, total R$ 1,17E+34).
// Inteiro de 0 a 99; lixo vira 0, que tira a linha - o mesmo que apagar o campo.
export function sacolaQuantidadeLimita(valor: any): number {
  const numero = Math.floor(Number(valor));
  if (!isFinite(numero) || numero < 0) {
    return 0;
  }
  return Math.min(numero, SACOLA_QUANTIDADE_MAXIMA);
}

// Quantas unidades do item ja estao na sacola: o selo "2x" do cartao da vitrine. Conta so
// as linhas principais - a sublinha de um combo (o refrigerante escolhido dentro dele)
// nao e o refrigerante pedido avulso.
export function sacolaItemQuantidade(sacola: Sacola, itemId: string): number {
  return (sacola?.linhasPrincipais || [])
    .filter((linha) => linha.item?.id === itemId)
    .reduce((soma, linha) => soma + linha.quantidade, 0);
}

export class Sacola {
  cliente: SacolaCliente;
  linhas: SacolaLinha[] = [];
  linhasPrincipais: SacolaLinha[] = [];
  quantidade: number = 0;
  total: number = 0.0;

  inicia() {
    this.linhas = [];
    this.linhasPrincipais = [];
    this.quantidade = 0;
    this.total = 0.0;
  }

  adiciona(item: CardapioItem, quantidade: number = 1) {
    // Mesmo item com observacao diferente ("sem cebola" e outro sem nada) e outra linha:
    // somar na primeira perderia a observacao da segunda.
    const observacao = (item.observacoes || "").trim();
    const itemSacola = this.linhas.find(
      (i) => i.item.id === item.id && (i.item.observacoes || "").trim() === observacao,
    );
    if (itemSacola && item.montagemBloco == null) {
      itemSacola.quantidade += quantidade;
      itemSacola.total = itemSacola.quantidade * itemSacola.valor;
    } else {
      this.linhas.push({
        // So o horario repetia: a montagem inclui item e subitens no mesmo milissegundo,
        // e todos saiam com o mesmo _id - a sacola nao achava as sublinhas para escalar.
        _id: `${new Date().getTime()}-${++sacolaLinhaSequencia}`,
        item,
        valor: item.valor,
        quantidade: quantidade,
        total: item.valor * quantidade,
      });
    }
    this.totalAtualiza();
  }

  remove(linha: SacolaLinha, quantidade = 0) {
    const _linha = this.linhas.find((x) => x._id === linha._id);
    if (_linha) {
      if (quantidade > 0 && _linha.quantidade > quantidade) {
        this.montagemEscala(_linha, _linha.quantidade - quantidade);
        _linha.quantidade -= quantidade;
        _linha.total = _linha.quantidade * _linha.valor;
      } else {
        if (_linha.item.montagemBloco != null) {
          var linhasMontagem = this.linhas.filter(
            (x) =>
              x.item.montagemBloco === _linha.item.montagemBloco &&
              x._id !== _linha._id,
          );
          linhasMontagem.forEach((linha) => {
            this.linhas.splice(this.linhas.indexOf(linha), 1);
          });
        }
        this.linhas.splice(this.linhas.indexOf(_linha), 1);
      }
      this.totalAtualiza();
    }
  }

  linhaAtualizaQuantidade(linha: SacolaLinha, quantidade: number) {
    if (quantidade < 0) {
      return;
    }
    const _linha = this.linhas.find((x) => x._id === linha._id);
    if (!quantidade && _linha) {
      if (_linha.item.montagemBloco != null) {
        var linhasMontagem = this.linhas.filter(
          (x) =>
            x.item.montagemBloco === _linha.item.montagemBloco &&
            x._id !== _linha._id,
        );
        linhasMontagem.forEach((linha) => {
          this.linhas.splice(this.linhas.indexOf(linha), 1);
        });
      }
      this.linhas.splice(this.linhas.indexOf(_linha), 1);
      this.totalAtualiza();
      return;
    }
    if (_linha) {
      this.montagemEscala(_linha, quantidade);
      _linha.quantidade = quantidade;
      _linha.total = _linha.quantidade * _linha.valor;
      this.totalAtualiza();
    }
  }

  // Muda as sublinhas da montagem na mesma proporcao do item. Cada sublinha guarda a
  // quantidade de todas as unidades, e nem sempre e 1 por unidade: na pizza de 4 sabores
  // cada quarto vale 0,25. Copiar a quantidade do item fazia meio sabor virar dois sabores
  // inteiros, e subtrair deixava negativo. Roda ANTES de o item receber a quantidade nova.
  private montagemEscala(linha: SacolaLinha, quantidade: number) {
    if (linha.item.montagemBloco == null || !(linha.quantidade > 0)) {
      return;
    }
    const proporcao = quantidade / linha.quantidade;
    this.linhas
      .filter((x) => x.item.montagemBloco === linha.item.montagemBloco && x._id !== linha._id)
      .forEach((sublinha) => {
        sublinha.quantidade = sublinha.quantidade * proporcao;
        sublinha.total = sublinha.quantidade * sublinha.valor;
      });
  }

  identifica(cliente: SacolaCliente) {
    this.cliente = cliente;
  }

  totalAtualiza() {
    this.quantidade = 0;
    this.total = 0.0;
    this.linhas.forEach((item) => {
      if (item.item.montagemBloco == null || item.item.montagemTipo === 10) {
        this.quantidade += item.quantidade;
      }
      this.total += item.total;
    });
    this.linhasPrincipais = this.linhas.filter(
      (l) =>
        l.item.montagemTipo === 10 ||
        l.item.montagemTipo === null ||
        l.item.montagemTipo === undefined,
    );
  }

  getTotalLinha(linha: SacolaLinha): number {
    if (linha.item.montagemTipo === 10) {
      return this.linhas.reduce((total, l) => {
        if (l.item.montagemBloco === linha.item.montagemBloco) {
          return total + l.total;
        }
        return total;
      }, 0);
    } else {
      return linha.total;
    }
  }

  getLinhasSubItens(montagemBloco: string): SacolaLinha[] {
    return this.linhas.filter(
      (linha) =>
        linha.item.montagemBloco == montagemBloco &&
        linha.item.montagemTipo != 10,
    );
  }
}

export class SacolaCliente {
  identificador: string;
  nome: string;
  imagem: string;
  email: string;
}

export class SacolaLinha {
  _id: string;
  item: CardapioItem;
  valor: number;
  quantidade: number;
  total: number;
}
